/**
 * AttachmentBubble — WhatsApp-style attachment renderer (V1.7.8 → module V1.7.9).
 * - image/* → inline thumbnail + lightbox
 * - video/* → inline <video controls>
 * - anything else → download card
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
    Download,
    FileText,
    Image as ImageIcon,
    VideoCamera,
    X,
} from "@phosphor-icons/react";
import { bytesToLabel } from "./helpers";
import { VoicePlayer } from "@/components/chat/VoiceRecorder";

export default function AttachmentBubble({ att, messageId, isMine }) {
    const contentType = (att.content_type || "").toLowerCase();
    const isImage = contentType.startsWith("image/");
    const isVideo = contentType.startsWith("video/");
    const isVoice = att.kind === "voice" || contentType.startsWith("audio/");
    const [blobUrl, setBlobUrl] = useState(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const [lightboxOpen, setLightboxOpen] = useState(false);

    useEffect(() => {
        if (!isImage && !isVideo && !isVoice) return;
        let cancelled = false;
        let created = null;
        (async () => {
            try {
                const res = await api.get(
                    `/team-chat/messages/${messageId}/attachment`,
                    { responseType: "blob" },
                );
                if (cancelled) return;
                created = URL.createObjectURL(res.data);
                setBlobUrl(created);
            } catch {
                if (!cancelled) setLoadFailed(true);
            }
        })();
        return () => {
            cancelled = true;
            if (created) URL.revokeObjectURL(created);
        };
    }, [messageId, isImage, isVideo, isVoice]);

    const sizeLabel = bytesToLabel(att.size_bytes || 0);

    if (isVoice) {
        return (
            <div
                className="mt-2"
                data-testid={`teamtalk-attachment-${messageId}`}
                data-attachment-type="voice"
            >
                <VoicePlayer
                    src={blobUrl}
                    durationMs={att.duration_ms}
                    transcript={att.transcript}
                    isMine={isMine}
                    testId={`teamtalk-voice-${messageId}`}
                />
            </div>
        );
    }

    async function download() {
        try {
            const res = await api.get(
                `/team-chat/messages/${messageId}/attachment`,
                { responseType: "blob" },
            );
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = att.filename || "attachment";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Couldn't download file");
        }
    }

    if (isImage) {
        return (
            <>
                <div
                    className="mt-2 rounded overflow-hidden max-w-[280px] cursor-zoom-in relative bg-ink/5"
                    data-testid={`teamtalk-attachment-${messageId}`}
                    data-attachment-type="image"
                    onClick={() => blobUrl && setLightboxOpen(true)}
                >
                    {blobUrl ? (
                        <img
                            src={blobUrl}
                            alt={att.filename || "image"}
                            className="w-full h-auto max-h-72 object-cover block"
                            data-testid={`teamtalk-attachment-img-${messageId}`}
                        />
                    ) : loadFailed ? (
                        <div className={`p-6 text-center text-xs ${isMine ? "text-white/70" : "text-ink/50"}`}>
                            Couldn't load image
                        </div>
                    ) : (
                        <div className={`aspect-video flex items-center justify-center ${isMine ? "bg-white/5" : "bg-cream"}`}>
                            <ImageIcon size={28} weight="bold" className={isMine ? "text-white/50" : "text-ink/30"} />
                        </div>
                    )}
                    {/* V2.0.20 — File caption: dark bar on green bubbles was
                        showing near-invisible white/90 text. Use ink text on a
                        translucent white overlay for both mine/peer bubbles so
                        filename + size are always legible. */}
                    <div className={`px-2 py-1.5 flex items-center gap-2 text-[10px] ${isMine ? "bg-ink/5 text-ink/75" : "bg-white text-ink/70"}`}>
                        <ImageIcon size={11} weight="bold" />
                        <span className="truncate flex-1 font-semibold">{att.filename}</span>
                        <span className="tabular-nums">{sizeLabel}</span>
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); download(); }}
                            aria-label="Download"
                            data-testid={`teamtalk-attachment-download-${messageId}`}
                            className="hover:opacity-70"
                        >
                            <Download size={11} weight="bold" />
                        </button>
                    </div>
                </div>
                {lightboxOpen && blobUrl && (
                    <ImageLightbox
                        src={blobUrl}
                        filename={att.filename}
                        onClose={() => setLightboxOpen(false)}
                    />
                )}
            </>
        );
    }

    if (isVideo) {
        return (
            <div
                className="mt-2 rounded overflow-hidden max-w-[320px]"
                data-testid={`teamtalk-attachment-${messageId}`}
                data-attachment-type="video"
            >
                {blobUrl ? (
                    <video
                        src={blobUrl}
                        controls
                        preload="metadata"
                        className="w-full h-auto max-h-80 block bg-black"
                        data-testid={`teamtalk-attachment-video-${messageId}`}
                    />
                ) : loadFailed ? (
                    <div className={`p-6 text-center text-xs ${isMine ? "text-white/70" : "text-ink/50"}`}>
                        Couldn't load video
                    </div>
                ) : (
                    <div className="aspect-video bg-ink/10 flex items-center justify-center text-[10px] text-ink/50">
                        Loading video…
                    </div>
                )}
                <div className={`px-2 py-1.5 flex items-center gap-2 text-[10px] ${isMine ? "bg-ink/5 text-ink/75" : "bg-white text-ink/70"}`}>
                    <VideoCamera size={11} weight="bold" />
                    <span className="truncate flex-1 font-semibold">{att.filename}</span>
                    <span className="tabular-nums">{sizeLabel}</span>
                    <button
                        type="button" onClick={download}
                        aria-label="Download"
                        data-testid={`teamtalk-attachment-download-${messageId}`}
                        className="hover:opacity-70"
                    >
                        <Download size={11} weight="bold" />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`mt-2 rounded p-2 flex items-center gap-2 ${isMine ? "bg-ink/5" : "bg-cream"}`}
            data-testid={`teamtalk-attachment-${messageId}`}
            data-attachment-type="doc"
        >
            <FileText size={18} weight="bold" className={isMine ? "text-ink/70" : "text-cobalt"} />
            <div className="min-w-0 flex-1">
                <p className={`text-xs font-bold truncate ${isMine ? "text-ink" : "text-ink"}`}>{att.filename}</p>
                <p className={`text-[10px] ${isMine ? "text-ink/60" : "text-ink/50"}`}>{sizeLabel}</p>
            </div>
            <button
                type="button" onClick={download}
                className={`p-1 hover:opacity-80 ${isMine ? "text-ink/70" : "text-cobalt"}`}
                aria-label="Download"
                data-testid={`teamtalk-attachment-download-${messageId}`}
            >
                <Download size={14} weight="bold" />
            </button>
        </div>
    );
}

function ImageLightbox({ src, filename, onClose }) {
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);
    return (
        <div
            className="fixed inset-0 bg-ink/90 z-[60] flex items-center justify-center p-4 cursor-zoom-out"
            onClick={onClose}
            data-testid="teamtalk-image-lightbox"
        >
            <img
                src={src}
                alt={filename || "image"}
                className="max-w-full max-h-full object-contain shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            />
            <button
                type="button" onClick={onClose}
                aria-label="Close"
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                data-testid="teamtalk-image-lightbox-close"
            >
                <X size={20} weight="bold" />
            </button>
        </div>
    );
}
